import React, { useEffect, useMemo, useState } from "react";
import { useAuthController, useRebaseClient } from "@rebasepro/app";
import type { RebaseClient } from "@rebasepro/types";

/**
 * Who is on the team, and who is signed in — for "quién la lleva".
 *
 * Ownership is an email on the row (drizzle/0054): the admin accounts are
 * the choices, read once from `users`, with the test fixtures the local
 * databases carry (`example.invalid`, `.local`) left out. The current user
 * comes from the session, so "Asignarme" needs no picker.
 *
 * Nothing here is a foreign key. `PRODUCT.md` names the absence of ownership
 * as the largest mismatch between the panel and the people using it, and
 * the cheapest honest fix is a string a person chose.
 */
export interface Teammate {
    email: string;
    name: string;
}

export function useTeam(): { me: string | null; team: Teammate[]; shortName: (email: string | null | undefined) => string } {
    const client = useRebaseClient<RebaseClient>();
    const auth = useAuthController();
    const data = (client as any)?.data;
    const [team, setTeam] = useState<Teammate[]>([]);

    const me = ((auth as any)?.user?.email as string | undefined)?.toLowerCase() ?? null;

    useEffect(() => {
        if (!data) return;
        let live = true;
        data.collection("users").find({ limit: 100 })
            .then((page: any) => {
                if (!live) return;
                const rows = (page?.data ?? []) as { email?: string; displayName?: string; roles?: string[] }[];
                setTeam(rows
                    .filter(u => Array.isArray(u.roles) && u.roles.includes("admin"))
                    .filter(u => u.email && !/example\.invalid$|\.local$/i.test(u.email))
                    .map(u => ({ email: String(u.email).toLowerCase(), name: (u.displayName ?? "").trim() || String(u.email) }))
                    .sort((a, b) => a.name.localeCompare(b.name)));
            })
            .catch(() => { if (live) setTeam([]); });
        return () => { live = false; };
    }, [data]);

    const shortName = useMemo(() => (email: string | null | undefined): string => {
        if (!email) return "";
        const known = team.find(t => t.email === email.toLowerCase());
        const name = known?.name && known.name !== known.email ? known.name : email.split("@")[0];
        return name.split(/\s+/)[0] ?? name;
    }, [team]);

    return { me, team, shortName };
}

/**
 * "Asignada a": the owner as a chip, "Asignarme" when it is nobody, and a
 * picker for handing it to somebody else. One control, used by the inbox
 * and the prospect list, so the two screens cannot disagree about what
 * owning something looks like.
 */
export function OwnerControl({
    owner,
    onChange,
    disabled
}: {
    owner: string | null | undefined;
    onChange: (next: string | null) => Promise<void> | void;
    disabled?: boolean;
}) {
    const { me, team, shortName } = useTeam();
    const [busy, setBusy] = useState(false);
    const set = async (next: string | null) => {
        setBusy(true);
        try { await onChange(next); } finally { setBusy(false); }
    };
    const current = (owner ?? "").toLowerCase() || null;
    return (
        <span className="inline-flex items-center gap-2 text-sm">
            <span className="opacity-70">Asignada a</span>
            <select
                aria-label="Asignar a"
                className="text-sm rounded-md border px-2 py-0.5 bg-transparent"
                value={current ?? ""}
                disabled={disabled || busy}
                onChange={e => void set(e.target.value || null)}
            >
                <option value="">sin asignar</option>
                {current && !team.some(t => t.email === current) && <option value={current}>{shortName(current)}</option>}
                {team.map(t => <option key={t.email} value={t.email}>{t.name}</option>)}
            </select>
            {!current && me && (
                <button type="button" className="text-primary hover:underline" disabled={disabled || busy} onClick={() => void set(me)}>
                    Asignarme
                </button>
            )}
        </span>
    );
}
